import { AfterViewInit, Component, DestroyRef, ElementRef, HostListener, Injector, Input, OnChanges, OnInit, ViewChild, afterNextRender } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SimplebarAngularModule, SimplebarAngularComponent } from 'simplebar-angular';
import { MetisMenu } from 'metismenujs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { interval } from 'rxjs';
import { MenuItem } from './menu.model';
import { EMPTY_NAV_COUNTS, NavCounts, NavCountsService } from './nav-counts.service';
import { CrmApiService } from '../../core/backend/services/crm.service';
import { QuickJumpComponent, QuickJumpPage } from './quick-jump.component';
import { superAdminMenuItems } from './super-admin-nav';
import { AuthenticationService } from '../../core/services/auth.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule, SimplebarAngularModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('componentRef') scrollRef!: SimplebarAngularComponent;
  @ViewChild('sideMenu') sideMenu!: ElementRef;
  @Input() isCondensed = false;

  private metisMenu: MetisMenu | null = null;
  logo = '/assets/images/logo.svg';
  menuItems: MenuItem[] = [];
  counts: NavCounts = EMPTY_NAV_COUNTS;
  /** Section title keys the user collapsed; kept per browser. */
  collapsedSections = new Set<string>(readCollapsedSections());
  /** The platform CRM switch; its menu section stays hidden until the API says it is on. */
  crmEnabled = false;

  constructor(
    private auth: AuthenticationService,
    private router: Router,
    private destroyRef: DestroyRef,
    private navCounts: NavCountsService,
    private modals: NgbModal,
    private crm: CrmApiService,
    private injector: Injector,
  ) {
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this._activateMenuDropdown();
          this._scrollElement();
          this.navCounts.refresh();
        }
      });

    this.auth.currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.refreshMenu();
      });

    this.auth.identity()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  ngOnInit(): void {
    this.refreshMenu();
    this.navCounts.counts$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((counts) => {
        this.counts = counts;
      });
    this.navCounts.refresh(true);
    this.crm.enabled()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((enabled) => {
        this.crmEnabled = enabled;
        this.refreshMenu();
      });
    interval(5 * 60_000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.navCounts.refresh(true));
  }

  badgeCount(item: MenuItem): number {
    return item.badge ? this.counts[item.badge] : 0;
  }

  isSectionCollapsed(section: string | undefined): boolean {
    return !!section && this.collapsedSections.has(section);
  }

  toggleSection(section: string | undefined): void {
    if (!section) {
      return;
    }
    if (this.collapsedSections.has(section)) {
      this.collapsedSections.delete(section);
    } else {
      this.collapsedSections.add(section);
    }
    writeCollapsedSections(this.collapsedSections);
    setTimeout(() => this.metisMenu?.update());
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.openQuickJump();
    }
  }

  openQuickJump(): void {
    if (this.modals.hasOpenModals()) {
      return;
    }
    const ref = this.modals.open(QuickJumpComponent, { size: 'lg', scrollable: true, windowClass: 'quick-jump-window' });
    (ref.componentInstance as QuickJumpComponent).pages = quickJumpPages(this.menuItems);
  }

  ngAfterViewInit(): void {
    this.initMetisMenu();
  }

  ngOnChanges(): void {
    if (this.isCondensed) {
      this.destroyMetisMenu();
    } else {
      setTimeout(() => this.initMetisMenu());
    }
  }

  private refreshMenu(): void {
    const rawItems = superAdminMenuItems().filter((item) => !item.requiresCrm || this.crmEnabled);
    this.menuItems = this.filterByPermissions(rawItems);
    // After the new items are drawn: on a timer the highlight could land on rows about to be replaced (PF-0148).
    afterNextRender(() => {
      this.destroyMetisMenu();
      this.initMetisMenu();
    }, { injector: this.injector });
  }

  private filterByPermissions(items: MenuItem[]): MenuItem[] {
    const visible = items
      .map((item) => {
        if (item.isTitle) {
          return item;
        }
        if (item.subItems && item.subItems.length > 0) {
          const visibleChildren = this.filterByPermissions(item.subItems);
          if (visibleChildren.length > 0) {
            return {
              ...item,
              subItems: visibleChildren,
            };
          }
          return null;
        }

        if (this.auth.hasPermission(item.permission)) {
          return item;
        }
        return null;
      })
      .filter((item): item is MenuItem => item !== null);

    // Drop section titles that have no visible item before the next title.
    return visible.filter((item, index) => !item.isTitle
      || (index + 1 < visible.length && !visible[index + 1].isTitle));
  }

  private initMetisMenu(): void {
    if (this.sideMenu && !this.metisMenu) {
      this.metisMenu = new MetisMenu(this.sideMenu.nativeElement, {
        toggle: true,
        triggerElement: '.has-arrow, .has-dropdown, is-parent',
        parentTrigger: 'li',
        subMenu: 'ul'
      });
      this._activateMenuDropdown();
    }
  }

  private destroyMetisMenu(): void {
    if (this.metisMenu) {
      this.metisMenu.dispose();
      this.metisMenu = null;
    }
  }

  clickElWhenCollapsed(): void {
    if (document.body.classList.contains('vertical-collpsed')) {
      this._activateMenuDropdown();
    }
  }

  _scrollElement(): void {
    setTimeout(() => {
      const activeElements = document.getElementsByClassName('mm-active');
      if (activeElements.length > 0 && this.scrollRef?.SimpleBar) {
        const currentPosition = (activeElements[0] as HTMLElement).offsetTop;
        if (currentPosition > 500) {
          this.scrollRef.SimpleBar.getScrollElement().scrollTop = currentPosition + 300;
        }
      }
    }, 300);
  }

  _activateMenuDropdown(): void {
    const menu = this.sideMenu?.nativeElement as HTMLElement | undefined;
    if (!menu) return;
    const removeClass = (className: string) => {
      const els = menu.getElementsByClassName(className);
      while (els[0]) {
        els[0].classList.remove(className);
      }
    };

    removeClass('active');
    removeClass('mm-active');
    removeClass('mm-show');

    const currentPath = this.router.url.split(/[?#]/)[0];
    const links = Array.from(menu.getElementsByClassName('side-nav-link-ref')) as HTMLAnchorElement[];

    let activeLink = links.find((link) => link.pathname === currentPath);
    if (!activeLink) {
      activeLink = links
        .filter((link) => link.pathname !== '/' && currentPath.startsWith(link.pathname + '/'))
        .sort((left, right) => right.pathname.length - left.pathname.length)[0];
    }

    if (!activeLink) {
      const pathParts = currentPath.split('/').filter(Boolean);
      for (let i = pathParts.length - 1; i > 0; i--) {
        const testPath = '/' + pathParts.slice(0, i).join('/');
        activeLink = links.find((link) => link.pathname === testPath);
        if (activeLink) break;
      }
    }

    if (activeLink) {
      activeLink.classList.add('active');
      let parent = activeLink.closest('li');
      while (parent) {
        const isSubMenuRow = parent.parentElement?.classList.contains('sub-menu');
        if (!isSubMenuRow) {
          parent.classList.add('mm-active');
        }
        const dropdown = parent.querySelector('.sub-menu');
        if (dropdown) {
          dropdown.classList.add('mm-show');
        }
        const parentMenu = parent.closest('ul');
        parent = parentMenu ? parentMenu.closest('li') : null;
      }

      if (this.metisMenu) {
        this.metisMenu.update();
      }
    }
  }

  onToggleMobileMenu(): void {
    document.body.classList.toggle('sidebar-enable');
  }
}

const COLLAPSED_SECTIONS_KEY = 'super-admin.sidebar.collapsed-sections';

function readCollapsedSections(): string[] {
  try {
    const stored = JSON.parse(localStorage.getItem(COLLAPSED_SECTIONS_KEY) || '[]');
    return Array.isArray(stored) ? stored.filter((value): value is string => typeof value === 'string') : [];
  } catch {
    return [];
  }
}

function writeCollapsedSections(sections: Set<string>): void {
  try {
    localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify([...sections]));
  } catch {
    // Storage can be unavailable (private mode); collapsing still works for this page view.
  }
}

/** Every page link in the (already permission-filtered) menu, sub-pages labelled with their parent. */
export function quickJumpPages(items: MenuItem[]): QuickJumpPage[] {
  return items.flatMap((item) => {
    if (item.isTitle) {
      return [];
    }
    if (item.subItems?.length) {
      return item.subItems
        .filter((sub) => Boolean(sub.link))
        .map((sub) => ({ label: sub.label || '', group: item.label, link: sub.link as string }));
    }
    return item.link ? [{ label: item.label || '', link: item.link }] : [];
  });
}
